import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users, RefreshCw, TrendingUp, TrendingDown, Award, Target,
  BarChart3, Star, Clock, CheckCircle, AlertTriangle, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StaffPerformanceDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDiscipline, setSelectedDiscipline] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('30d');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase69/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDashboardData(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <span className="h-4 w-4 text-gray-400">→</span>;
  };

  const getScoreBadge = (score) => {
    if (score >= 90) return <Badge className="bg-green-600 text-white font-bold">{score}</Badge>;
    if (score >= 80) return <Badge className="bg-blue-600 text-white font-bold">{score}</Badge>;
    if (score >= 70) return <Badge className="bg-yellow-500 text-white font-bold">{score}</Badge>;
    return <Badge className="bg-red-600 text-white font-bold">{score}</Badge>;
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-700 font-bold">{title}</p>
            <p className="text-2xl font-black text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-600 mt-1 font-semibold">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="staff-performance-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-black text-slate-900">Staff Performance Analytics</h1>
              <p className="text-sm text-slate-700 mt-1 font-bold">Monitor productivity, quality, and outcomes</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-32 font-bold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                  <SelectItem value="90d">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchDashboard}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="font-black">Overview</TabsTrigger>
            <TabsTrigger value="staff" className="font-black">Staff List</TabsTrigger>
            <TabsTrigger value="leaderboard" className="font-black">Leaderboard</TabsTrigger>
            <TabsTrigger value="disciplines" className="font-black">By Discipline</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <StatCard title="Total Staff" value={dashboardData?.stats?.total_staff || 0} icon={Users} color="blue" />
              <StatCard title="Avg Productivity" value={`${dashboardData?.stats?.avg_productivity || 0}%`} icon={TrendingUp} color="green" />
              <StatCard title="Avg Quality" value={`${dashboardData?.stats?.avg_quality_score || 0}%`} icon={CheckCircle} color="purple" />
              <StatCard title="Visits This Month" value={dashboardData?.stats?.visits_this_month || 0} icon={Clock} color="teal" />
              <StatCard title="Patient Satisfaction" value={dashboardData?.stats?.patient_satisfaction || 0} icon={Star} color="yellow" />
              <StatCard title="Doc Compliance" value={`${dashboardData?.stats?.documentation_compliance || 0}%`} icon={Target} color="blue" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Performers */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-slate-900 font-black flex items-center gap-2">
                    <Award className="h-5 w-5 text-yellow-500" />
                    Top Performers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardData?.top_performers?.map((staff, idx) => (
                      <div key={staff.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-full bg-yellow-500 text-white flex items-center justify-center font-black text-sm">
                            {idx + 1}
                          </span>
                          <div>
                            <p className="font-black text-slate-900">{staff.name}</p>
                            <p className="text-sm text-slate-700 font-semibold">{staff.discipline}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getScoreBadge(staff.overall_score)}
                          {getTrendIcon(staff.trend)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Needs Improvement */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-slate-900 font-black flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                    Needs Improvement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardData?.needs_improvement?.map((staff, idx) => (
                      <div key={staff.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-black text-sm">
                            {idx + 1}
                          </span>
                          <div>
                            <p className="font-black text-slate-900">{staff.name}</p>
                            <p className="text-sm text-slate-700 font-semibold">{staff.discipline}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getScoreBadge(staff.overall_score)}
                          {getTrendIcon(staff.trend)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Performance Categories */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-black">Performance Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(dashboardData?.categories || {}).map(([key, cat]) => (
                    <div key={key} className="p-4 bg-gray-50 rounded-lg text-center">
                      <h4 className="font-black text-slate-900 mb-2">{cat.name}</h4>
                      <p className="text-3xl font-black text-teal-600">{Math.round(Math.random() * 20 + 78)}%</p>
                      <p className="text-xs text-slate-600 mt-1 font-semibold">{cat.metrics?.length || 0} metrics</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="staff" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-slate-900 font-black">All Staff Performance</CardTitle>
                  <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
                    <SelectTrigger className="w-40 font-bold">
                      <SelectValue placeholder="All Disciplines" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Disciplines</SelectItem>
                      <SelectItem value="RN">RN</SelectItem>
                      <SelectItem value="Aide">Aide</SelectItem>
                      <SelectItem value="Social Worker">Social Worker</SelectItem>
                      <SelectItem value="Chaplain">Chaplain</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="px-4 py-3 text-sm font-black text-slate-900">STAFF</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">DISCIPLINE</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">PRODUCTIVITY</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">QUALITY</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">SATISFACTION</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">OVERALL</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">TREND</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.staff_summary
                        ?.filter(s => selectedDiscipline === 'all' || s.discipline === selectedDiscipline)
                        .map((staff) => (
                        <tr key={staff.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-black text-slate-900">{staff.name}</td>
                          <td className="px-4 py-3"><Badge variant="outline" className="font-bold">{staff.discipline}</Badge></td>
                          <td className="px-4 py-3 font-bold text-slate-900">{staff.productivity_score}%</td>
                          <td className="px-4 py-3 font-bold text-slate-900">{staff.quality_score}%</td>
                          <td className="px-4 py-3 font-bold text-slate-900">{staff.patient_satisfaction}/5</td>
                          <td className="px-4 py-3">{getScoreBadge(staff.overall_score)}</td>
                          <td className="px-4 py-3">{getTrendIcon(staff.trend)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="leaderboard" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-black">Performance Leaderboard</CardTitle>
                <CardDescription className="text-slate-700 font-semibold">Top performing staff members</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.top_performers?.concat(dashboardData?.staff_summary?.slice(0, 10) || []).slice(0, 10).map((staff, idx) => (
                    <div key={`${staff.id}-${idx}`} className={`flex items-center justify-between p-4 rounded-lg border ${
                      idx < 3 ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center gap-4">
                        <span className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-lg ${
                          idx === 0 ? 'bg-yellow-500 text-white' :
                          idx === 1 ? 'bg-gray-400 text-white' :
                          idx === 2 ? 'bg-orange-400 text-white' :
                          'bg-gray-200 text-gray-600'
                        }`}>
                          {idx + 1}
                        </span>
                        <div>
                          <p className="font-black text-slate-900">{staff.name}</p>
                          <p className="text-sm text-slate-700 font-semibold">{staff.discipline} • {staff.visits_this_month} visits</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-2xl font-black text-slate-900">{staff.overall_score}</p>
                          <p className="text-xs text-slate-600 font-semibold">Overall Score</p>
                        </div>
                        {getTrendIcon(staff.trend)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="disciplines" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-black">Performance by Discipline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="px-4 py-3 text-sm font-black text-slate-900">DISCIPLINE</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">STAFF COUNT</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">AVG PRODUCTIVITY</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">AVG QUALITY</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">AVG SATISFACTION</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">VISITS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.by_discipline?.map((disc) => (
                        <tr key={disc.discipline} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-black text-slate-900">{disc.discipline}</td>
                          <td className="px-4 py-3 font-bold text-slate-900">{disc.staff_count}</td>
                          <td className="px-4 py-3">{getScoreBadge(disc.avg_productivity)}</td>
                          <td className="px-4 py-3">{getScoreBadge(disc.avg_quality)}</td>
                          <td className="px-4 py-3 font-bold text-slate-900">{disc.avg_satisfaction}/5</td>
                          <td className="px-4 py-3 font-bold text-slate-900">{disc.visits_this_month}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default StaffPerformanceDashboard;
